import React, { useState, useEffect } from "react";
import useAxios from "../utils/useAxios"; // Import the custom Axios instance
import { Link } from "react-router-dom";
import { hideModal, showModal } from "../utils/modalUtility";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const initialFormData = {
    id: "",
    fullName: "",
    phone: "",
    password: "",
    role: "user",
  };

  const [formData, setFormData] = useState(initialFormData);
  const axiosAPI = useAxios(); // Use the custom hook for Axios

  // Function to handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  // Function to fetch users data from the API
  const fetchUsers = async () => {
    try {
      const response = await axiosAPI.get(`/api/users/get-all-users`);
      const usersData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if required fields are filled (password is mandatory for new user, optional for edit)
    if (
      !formData.fullName?.trim() ||
      !formData.phone?.trim() ||
      (!editMode && !formData.password?.trim())
    ) {
      window.notifyMessage(
        "Error",
        editMode
          ? "Full Name and Phone number are mandatory."
          : "Full Name, Phone number, and Password are mandatory.",
        "danger",
      );
      return;
    }

    // Regular expression for validating 10-digit phone number
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      window.notifyMessage(
        "Error",
        "Please enter a valid 10-digit phone number.",
        "danger",
      );
      return;
    }

    try {
      if (editMode) {
        // PUT API call for updating user
        const dataToUpdate = {
          fullName: formData.fullName.trim(),
          phone: formData.phone.trim(),
          role: formData.role || "user",
          password: formData.password || undefined,
        };

        if (!dataToUpdate.password) {
          delete dataToUpdate.password;
        }

        await axiosAPI.put(
          `/api/users/update-user/${currentUserId}`,
          dataToUpdate,
        );

        window.notifyMessage("Success", "User updated successfully");
        hideModal("editUserModal");
      } else {
        // POST API call for adding new user
        await axiosAPI.post(`/api/users/add-user`, {
          fullName: formData.fullName.trim(),
          phone: formData.phone.trim(),
          password: formData.password,
          role: formData.role || "user",
        });

        window.notifyMessage("Success", "Successfully added the user!");
        hideModal("newUserModal");
      }

      setFormData(initialFormData);
      setCurrentUserId(null);
      setEditMode(false);
      fetchUsers();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage(
          "Error",
          editMode
            ? "There was an error updating the user."
            : "There was an error adding the user!",
          "danger",
        );
      }
    }
  };

  const openNewModal = () => {
    setEditMode(false);
    setCurrentUserId(null);
    setFormData(initialFormData);
    showModal("newUserModal");
  };

  const openEditModal = (userId) => {
    const userToEdit = users.find((user) => user.id === userId);

    if (userToEdit) {
      setEditMode(true);
      setCurrentUserId(userId);
      setFormData({
        id: userToEdit.id,
        fullName: userToEdit.full_name || "",
        phone: userToEdit.phone_number || "",
        role: userToEdit.role || "user",
        password: "", // Reset password field (optional for editing)
      });

      showModal("editUserModal");
    }
  };

  const handleDeleteUser = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this mentor?",
    );
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(`/api/users/delete-user/${id}`);
      window.notifyMessage(
        "Success",
        response.data?.message || "Deleted successfully",
        "success",
      );
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "An error occurred while deleting.";
      window.notifyMessage("Error", errMsg, "danger");
      fetchUsers();
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div className="d-flex"></div>
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={openNewModal}
            >
              Add New User
            </button>
          </div>
        </div>

        {/* New User Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              {/* Modal Header */}
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  Add User
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                {/* {JSON.stringify(formData)} */}
                <div className="row">
                  <div className="col-md-12">
                    <form onSubmit={handleSubmit}>
                      {/* Full Name */}
                      <div className="mb-3">
                        <label htmlFor="fullName" className="form-label">
                          Full Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="fullName"
                          placeholder="Enter Full Name"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          placeholder="Enter 10-digit Phone Number"
                          maxLength="10"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      {/* Role Dropdown */}
                      <div className="mb-3">
                        <label htmlFor="role" className="form-label">
                          Role
                        </label>
                        <select
                          className="form-select form-control"
                          id="role"
                          value={formData.role}
                          onChange={handleChange}
                          required
                        >
                          <option value="donor">Donor</option>
                          <option value="mentor">Mentor</option>
                        </select>
                      </div>

                      {/* Password */}
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">
                          Password
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          placeholder="Enter Password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary btn-round btn-sm"
                      >
                        Add User
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit User Modal */}
        <div
          className="modal fade"
          id="editUserModal"
          tabIndex="-1"
          aria-labelledby="editUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              {/* Modal Header */}
              <div className="modal-header">
                <h5 className="modal-title" id="editUserModalLabel">
                  Edit User
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <form onSubmit={handleSubmit}>
                      {/* Full Name */}
                      <div className="mb-3">
                        <label htmlFor="fullName" className="form-label">
                          Full Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="fullName"
                          placeholder="Enter Full Name"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          placeholder="Enter 10-digit Phone Number"
                          maxLength="10"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      {/* Role Dropdown */}
                      <div className="mb-3">
                        <label htmlFor="role" className="form-label">
                          Role
                        </label>
                        <select
                          className="form-select form-control"
                          id="role"
                          value={formData.role}
                          onChange={handleChange}
                          required
                        >
                          <option value="donor">Donor</option>
                          <option value="mentor">Mentor</option>
                        </select>
                      </div>

                      {/* Password */}
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">
                          Password
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          placeholder="Enter New Password (leave blank to keep current)"
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <small className="text-muted">
                          Leave blank if you do not want to change the password.
                        </small>
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary btn-round btn-sm"
                      >
                        Update User
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="card-title">
                      All Users (<span>{users.length}</span>)
                    </h4>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="display table table-striped table-hover basic-datatables">
                    <thead>
                      <tr>
                        <th>Full Name</th>
                        <th>Phone Number</th>
                        <th>Role</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            No users found. Click "Add New User" to create one.
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => {
                          const formattedDate = user.created_at
                            ? new Date(user.created_at).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "-";

                          return (
                            <tr key={user.id}>
                              <td>{user.full_name || "-"}</td>
                              <td>{user.phone_number || user.email || "-"}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    user.role === "mentor"
                                      ? "bg-info"
                                      : user.role === "admin"
                                        ? "bg-danger"
                                        : "bg-secondary"
                                  }`}
                                >
                                  {user.role
                                    ? user.role.charAt(0).toUpperCase() +
                                      user.role.slice(1)
                                    : "User"}
                                </span>
                              </td>
                              <td>{formattedDate}</td>
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(user.id)}
                                  title="Edit User"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDeleteUser(user.id)}
                                  title="Delete Mentor"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                                <Link to="/user-setting" className="logo">
                                  <button className="btn btn-icon btn-round btn-dark btn-sm d-none">
                                    <i className="fas fa-cog"></i>
                                  </button>
                                </Link>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
